'use client';

import { normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import type { CartItemPackages } from '@eliadmin/core/src/api/cart/cart-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import {
  CartConfirmationDialog as AddToCartDialog,
  CartVariantDialog as AddToCartVariantDialog,
} from '@/components/product/cart-dialogs';
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useAddToCart } from '@/hooks/use-add-to-cart';
import { getKnownStoreCurrency } from '@/lib/store-currency';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SelectedServices } from '@eliadmin/core/src/stores/variant-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ButtonIcon } from '../ui/button-icon';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';

type AddToCartButtonProps = ButtonProps & {
  product: ProductDetailData | Product;
  quantity?: number;
  className?: string;
  showIcon?: boolean;
  sellable?: boolean;
  customText?: string;
  type?: string;
  customValuesError?: boolean;
  skipVariantDialog?: boolean;
  inCard?: boolean;
  fullSize?: boolean;
};

export function AddToCartButton({
  className,
  showIcon,
  sellable,
  product,
  customText,
  quantity = 1,
  customValuesError = false,
  skipVariantDialog = false,
  inCard = false,
  fullSize = false,
  ...props
}: AddToCartButtonProps) {
  const isDesktop = useBreakpoint('sm');
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const openDialog = useDialogStore(s => s.open);
  const me = useCustomerStore(s => s.me);

  const addToCartMutation = useAddToCart();
  const [isAutoAdding, setIsAutoAdding] = useState(false);

  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedPackages = useVariantsStore(state => state.selectedPackages);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const customValues = useVariantsStore(state => state.customValues);
  const selectedAccessories = useVariantsStore(state => state.selectedAccessories);
  const accessoriesQuantities = useVariantsStore(state => state.accessoriesQuantities);

  const productQuery = useQuery({
    queryKey: [CacheKey.PRODUCT_DETAIL, locale, product.id, me?.authToken ?? null],
    queryFn: () =>
      fetchSlugDetail({ slug: product.slug, locale, authToken: me?.authToken })
        .then(res => res['App\\Models\\Product'])
        .catch(() => null),
    enabled: false,
  });

  const sortedSuppliers = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position);

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const knownCurrency = getKnownStoreCurrency(currencySymbol);
  const [resolvedCurrency, setResolvedCurrency] = useState<string>('EUR');
  const currencyISO = knownCurrency ?? resolvedCurrency;

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES, locale],
    queryFn: () => fetchCurrencies({ locale }),
    enabled: !knownCurrency,
  });

  useEffect(() => {
    if (currencies) {
      setResolvedCurrency(
        currencies.data.find(currency => currency.symbol === currencySymbol)?.id ?? currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  const hasUnresolvedVariants =
    (!!product.has_variants || !!product.variants?.length) && !selectedVariant;
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product);
  const priceOnRequest = !hasUnresolvedVariants && priceDisplay.priceOnRequest;

  const handleAddToCart = async () => {
    if (priceOnRequest || (!sellable && quantity < 1)) return;

    const fullProduct = product.has_variants
      ? await productQuery.refetch().then(res => res.data)
      : product;

    const desiredQuantity = normalizeProductQuantity(quantity, product);

    if (fullProduct && (fullProduct.variants?.length || selectedAccessories?.length)) {
      openDialog({
        id: AddToCartVariantDialog.id,
        content: (
          <VariantsProvider
            state={{
              product: fullProduct,
              selectedPackages,
              selectedAccessories,
              accessoriesQuantities,
              selectedVariant,
              selectedQuantity,
              selectedServices,
              selectedValues,
              customValues,
              customValuesError,
            }}
          >
            <AddToCartVariantDialog
              product={fullProduct}
              quantity={desiredQuantity}
              skipVariantDialog={skipVariantDialog}
              onAutoAddStateChange={setIsAutoAdding}
            />
          </VariantsProvider>
        ),
      });
    } else {
      await addToCartMutation.mutateAsync({
        locale,
        body: {
          item: {
            product: { id: product.id },
            sub_items: {
              services: selectedServices
                ? selectedServices.map((s: SelectedServices) => ({ id: s.id }))
                : [],
              packages: selectedPackages
                ? selectedPackages.map(p => ({ id: p.id }) as CartItemPackages)
                : [],
            },
          },
          qty: desiredQuantity,
        },
      });
      trackEcommerce.addToCart({
        id: product.id.toString(),
        name: product.name,
        price: priceDisplay.currentWithVat ?? 0,
        currency: currencyISO,
      });
      openDialog({
        id: AddToCartDialog.id,
        content: <AddToCartDialog product={product} title="text631" />,
      });
    }
  };

  const buttonSize = fullSize || (inCard && isDesktop) ? 'default' : 'icon';
  const hideText = !fullSize && inCard && !isDesktop;

  const firstSupplier = sortedSuppliers[0];
  const isProductEnded = product.status === 'ended';

  const buttonContent = (
    <>
      {showIcon && <ButtonIcon alt={t('text210')} url="/images/icons/cart-add-white.svg" />}
      <span className={cn('line-clamp-1 text-ellipsis', hideText && 'hidden')}>
        {customText ? t(customText) : t('text1115')}
      </span>
    </>
  );

  if (firstSupplier?.is_affiliate) {
    return (
      <Button
        className={cn(className)}
        asChild
        variant="default"
        size={buttonSize}
        isLoading={addToCartMutation.isPending || productQuery.isFetching}
        {...props}
      >
        <a href={firstSupplier.url ?? '#'}>
          {showIcon && <ButtonIcon alt={t('text210')} url="/images/icons/cart-add-white.svg" />}
          <span className="line-clamp-1 text-ellipsis text-center">
            {customText ? t(customText) : t('text1226')}
          </span>
        </a>
      </Button>
    );
  }

  if (priceOnRequest) {
    return null;
  }

  return (
    <Button
      className={cn(className)}
      onClick={e => {
        e.stopPropagation();
        e.nativeEvent.preventDefault();
        handleAddToCart();
      }}
      disabled={!sellable || isProductEnded || priceOnRequest || customValuesError}
      variant="default"
      size={buttonSize}
      isLoading={addToCartMutation.isPending || productQuery.isFetching || isAutoAdding}
      {...props}
    >
      {buttonContent}
    </Button>
  );
}
